import { UpgradeLink } from "@/components/common/UpgradeLink/UpgradeLink"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Typography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { useGetUserFreeTrialsLeftData } from "@/services/api/userService"
import { getTrialStatus } from "@/utils/freeTrialStatus"
import { Sparkles } from "lucide-react"
import { SettingsPage, SettingsSection } from "../../components/SettingsPage"

/** The user's own trial. Each person's trial counts from their own account,
 *  so this is an Account page, and only users on a trial get it. */
export function UsagePage() {
  const { translate } = useTranslation()
  // Straight from the query, which the nav already loaded to show this page:
  // copying it into state first painted an empty "0 / 0 days, Active" trial.
  const { data } = useGetUserFreeTrialsLeftData()
  const trial = data?.freeTrial
  const left = trial?.freeTrialDurationLeftDays ?? 0
  const total = trial?.totalFreeTrialDurationDays ?? 0
  const status = getTrialStatus(trial)
  const isExpired = status === "expired"
  // An unknown trial clock has no day count to show
  const isUnknown = status === "unknown"
  const daysUsed = total - left

  return (
    <SettingsPage
      title={translate("SETTINGS/USAGE/TITLE")}
      description={translate("SETTINGS/USAGE/DESCRIPTION")}
    >
      <SettingsSection
        title={translate("SETTINGS/TRIAL_SECTION")}
        description={translate(
          isExpired
            ? "SETTINGS/TRIAL_EXPIRED_DESCRIPTION"
            : "SETTINGS/TRIAL_DESCRIPTION",
        )}
        action={
          <Badge variant={isExpired ? "destructive" : "outline"}>
            {translate(
              isExpired ? "SETTINGS/TRIAL_EXPIRED" : "SETTINGS/TRIAL_ACTIVE",
            )}
          </Badge>
        }
        footer={
          isExpired && (
            <Button asChild>
              <UpgradeLink>
                <Sparkles />
                {translate("SETTINGS/UPGRADE_BUTTON")}
              </UpgradeLink>
            </Button>
          )
        }
      >
        {!isUnknown && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-4">
              <Typography as="muted">
                {left} {translate("SETTINGS/DAYS_LEFT")}
              </Typography>
              <Typography as="small">
                {daysUsed} / {total} {translate("SETTINGS/DAYS")}
              </Typography>
            </div>
            <Progress
              value={total > 0 ? (daysUsed / total) * 100 : 0}
              className={cn(isExpired && "[&>div]:bg-destructive")}
            />
          </div>
        )}
        {isExpired && (
          <Typography as="muted">
            {translate("SETTINGS/UPGRADE_MESSAGE")}
          </Typography>
        )}
      </SettingsSection>
    </SettingsPage>
  )
}
